// Bahasa Malaysia / English UI strings for the Heritage catalogue.
// Keep keys stable — components import { t } from useI18n() and pass keys.

export const DICT = {
  en: {
    // Nav
    "nav.browse": "Browse",
    "nav.dashboard": "Dashboard",
    "nav.login": "Sign in",
    "nav.logout": "Log out",
    "brand.tagline": "Pre-Inventory · Heritage",

    // Hero / Public browse
    "hero.overline": "Kuching, Sarawak · Pre-Inventory",
    "hero.title.a": "A living catalogue",
    "hero.title.b": "of Kuching's heritage.",
    "hero.subtitle":
      "Documenting the heritage buildings, monuments, sites and old shophouses that shape the city cultural landscape. Browse the inventory, explore the map, and trace each site's story.",
    "hero.stat.records": "Records",
    "hero.stat.authorities": "Authorities",
    "hero.stat.oldest": "Oldest",
    "hero.stat.under_dbku": "Under DBKU",
    "hero.stat.under_mbks": "Under MBKS",
    "hero.stat.under_bau": "Under Bau DC",

    // Controls
    "controls.search": "Search by name, location, owner/manager…",
    "controls.type.all": "All",
    "controls.mine": "My records",
    "controls.view.grid": "Grid",
    "controls.view.list": "List",
    "controls.view.map": "Map",
    "controls.add": "Add record",
    "controls.csv": "CSV",

    // Empty
    "empty.title": "No records found",
    "empty.body": "Try adjusting your search or filter — or add a new record.",
    "empty.loading": "Loading records…",

    // Card / list
    "card.built": "Built",
    "card.locationTBD": "Location TBD",
    "card.photos": "photos",

    // Detail modal
    "detail.location": "Location",
    "detail.year": "Year built",
    "detail.managed": "Occupier / Manager",
    "detail.owner": "Owner",
    "detail.condition": "Condition",
    "detail.description": "Description",
    "detail.significance": "Historical significance",
    "detail.latitude": "Latitude",
    "detail.longitude": "Longitude",

    // Form
    "form.new": "New record",
    "form.edit": "Edit record",
    "form.title.new": "Add heritage record",
    "form.title.default": "Heritage record",
    "form.subtitle":
      "All fields marked with an asterisk are required. GPS coordinates are used for the map view.",
    "form.gallery": "Photograph gallery",
    "form.gallery.empty": "No photos yet. The first image becomes the cover.",
    "form.upload": "Add photos",
    "form.upload.progress": "Uploading…",
    "form.upload.hint": "JPG, PNG or WEBP. Max 8MB each. Select multiple to upload in batch.",
    "form.cover": "Cover",
    "form.field.name": "Name *",
    "form.field.type": "Type *",
    "form.field.description": "Description",
    "form.field.significance": "Historical significance",
    "form.field.location": "Location",
    "form.field.location.placeholder": "e.g. Padungan, Kuching",
    "form.field.lat": "Latitude *",
    "form.field.lng": "Longitude *",
    "form.field.managed": "Occupier / Manager",
    "form.field.owner": "Owner",
    "form.field.authority": "Local authority",
    "form.field.year": "Year built",
    "form.field.year.placeholder": "e.g. 1879",
    "form.field.condition": "Condition",
    "form.field.status": "Status",
    "form.field.status.select": "Select status",
    "form.field.gazette_no": "Gazette No.",
    "form.field.gazette_no.placeholder": "e.g. P.U.(B) 123/2021",
    "form.field.land_status": "Land Status",
    "form.field.land_status.placeholder": "e.g. Reserve Land, NCR, Mixed Zone…",
    "detail.status": "Status",
    "detail.gazette_no": "Gazette No.",
    "detail.land_status": "Land Status",
    "status.Gazetted": "Gazetted",
    "status.Director List": "Director List",
    "status.Not Listed": "Not Listed",
    "form.section.ms": "Bahasa Malaysia (optional)",
    "form.section.ms.hint":
      "Bahasa translations shown to visitors when they switch language. Empty fields fall back to English.",
    "form.translate": "Auto-translate to Bahasa",
    "form.translate.progress": "Translating…",
    "form.translate.success": "Translated {n} fields to Bahasa",
    "form.translate.failed": "Translation failed",
    "form.field.name.ms": "Nama (Bahasa)",
    "form.field.description.ms": "Penerangan (Bahasa)",
    "form.field.significance.ms": "Kepentingan sejarah (Bahasa)",
    "form.field.location.ms": "Lokasi (Bahasa)",
    "form.field.managed.ms": "Occupier / Manager (Bahasa)",
    "form.field.owner.ms": "Owner (Bahasa)",
    "form.cancel": "Cancel",
    "form.save.new": "Create record",
    "form.save.edit": "Save changes",
    "form.saving": "Saving…",
    "form.err.name": "Name is required",

    // Delete
    "delete.title": "Delete heritage record?",
    "delete.body.a": "You are about to permanently delete",
    "delete.body.b": "This action cannot be undone.",
    "delete.cancel": "Cancel",
    "delete.confirm": "Delete permanently",

    // Login
    "login.back": "Back to browse",
    "login.back.short": "Back",
    "login.editorial.title.a": "Guardians of",
    "login.editorial.title.b": "the city's memory.",
    "login.editorial.body":
      "This portal is reserved for authorised heritage administrators. Sign in to add, edit, and steward the pre-inventory catalogue for DBKU and MBKS.",
    "login.editorial.footer": "Sarawak · est. 1841 · a living archive",
    "login.overline": "Welcome back",
    "login.title": "Access the inventory.",
    "login.subtitle": "Sign in to contribute new sites, edit your own records, or help preserve Kuching's living archive — everyone's welcome.",
    "login.email": "Email",
    "login.password": "Password",
    "login.name": "Full name",
    "login.submit": "Sign in",
    "login.submitting": "Signing in…",
    "login.welcome": "Welcome back",
    "login.failed": "Login failed",
    "login.mode.signin": "Sign in",
    "login.mode.register": "Create account",
    "login.switch.toRegister": "New here?",
    "login.switch.toRegisterCta": "Create an account",
    "login.switch.toSignin": "Already have an account?",
    "login.switch.toSigninCta": "Sign in",
    "login.register.overline": "Create account",
    "login.register.title": "Join the archive.",
    "login.register.subtitle": "Sign up to save favourite sites and follow the catalogue as it grows.",
    "login.register.submit": "Create account",
    "login.register.submitting": "Creating account…",
    "login.register.welcome": "Welcome to Warisan Kuching",
    "login.register.failed": "Registration failed",
    "login.forgot.link": "Forgot password?",
    "login.forgot.overline": "Password reset",
    "login.forgot.title": "Reset your password.",
    "login.forgot.subtitle":
      "Enter the email attached to your account. We'll send a secure link to choose a new password.",
    "login.forgot.submit": "Send reset link",
    "login.forgot.submitting": "Sending link…",
    "login.forgot.back": "Back to sign in",
    "login.forgot.sent.title": "Check your inbox.",
    "login.forgot.sent.body":
      "If an account with that email exists, we've just sent a reset link. It's valid for one hour.",
    "reset.overline": "Choose a new password",
    "reset.title": "Set a new password.",
    "reset.subtitle": "Pick a strong password (at least 6 characters). You'll be signed out of any active session.",
    "reset.new": "New password",
    "reset.confirm": "Confirm password",
    "reset.submit": "Update password",
    "reset.submitting": "Updating…",
    "reset.mismatch": "Passwords don't match",
    "reset.invalid": "This reset link is invalid or has expired.",
    "reset.success.title": "Password updated.",
    "reset.success.body": "You can now sign in with your new password.",
    "reset.success.cta": "Go to sign in",

    // Admin dashboard
    "admin.overline": "Curator dashboard",
    "admin.title": "Welcome, {name}.",
    "admin.subtitle":
      "Overview of the pre-inventory catalogue across DBKU, MBKS and partner authorities.",
    "admin.new": "New heritage record",
    "admin.export": "Export CSV",
    "admin.stat.total": "Total records",
    "admin.stat.authorities": "Authorities",
    "admin.stat.types": "Types covered",
    "admin.stat.risk": "At risk (Poor + Critical)",
    "admin.breakdown.authority": "By local authority",
    "admin.breakdown.type": "By heritage type",
    "admin.records.overline": "Inventory",
    "admin.records.title": "All records",
    "admin.records.public": "Public view",
    "admin.records.empty.title": "No records yet",
    "admin.records.empty.body": "Add your first heritage record to get started.",
    "admin.records.loading": "Loading…",
    "admin.col.record": "Record",
    "admin.col.type": "Type",
    "admin.col.authority": "Authority",
    "admin.col.year": "Year",
    "admin.col.condition": "Condition",
    "admin.col.actions": "Actions",

    // Toasts / generic
    "toast.saved.new": "Record created",
    "toast.saved.edit": "Record updated",
    "toast.deleted": "Record deleted",
    "toast.load.failed": "Failed to load records",
    "toast.delete.failed": "Delete failed",
    "toast.upload.success": "{n} image uploaded",
    "toast.upload.plural": "{n} images uploaded",
    "toast.upload.failed": "Upload failed",

    "footer.brand": "Warisan Kuching",
    "footer.credit": "Curated for DBKU, MBKS & heritage partners",
    "footer.tagline": "Pre-Inventory Heritage System",
  },
  ms: {
    "nav.browse": "Layar",
    "nav.dashboard": "Papan Pemuka",
    "nav.login": "Log masuk",
    "nav.logout": "Log keluar",
    "brand.tagline": "Pra-Inventori · Warisan",

    "hero.overline": "Kuching, Sarawak · Pra-Inventori",
    "hero.title.a": "Katalog hidup",
    "hero.title.b": "warisan Kuching.",
    "hero.subtitle":
      "Mendokumentasi bangunan warisan, monumen, tapak dan rumah kedai lama yang membentuk lanskap budaya bandar. Layar inventori, terokai peta, dan telusuri kisah setiap tapak.",
    "hero.stat.records": "Rekod",
    "hero.stat.authorities": "Pihak Berkuasa",
    "hero.stat.oldest": "Tertua",
    "hero.stat.under_dbku": "Di bawah DBKU",
    "hero.stat.under_mbks": "Di bawah MBKS",
    "hero.stat.under_bau": "Di bawah Bau DC",

    "controls.search": "Cari nama, lokasi, pemilik/pengurus…",
    "controls.type.all": "Semua",
    "controls.mine": "Rekod saya",
    "controls.view.grid": "Grid",
    "controls.view.list": "Senarai",
    "controls.view.map": "Peta",
    "controls.add": "Tambah rekod",
    "controls.csv": "CSV",

    "empty.title": "Tiada rekod dijumpai",
    "empty.body": "Cuba laraskan carian atau penapis — atau tambah rekod baharu.",
    "empty.loading": "Memuatkan rekod…",

    "card.built": "Dibina",
    "card.locationTBD": "Lokasi belum ditetapkan",
    "card.photos": "foto",

    "detail.location": "Lokasi",
    "detail.year": "Tahun dibina",
    "detail.managed": "Penghuni / Pengurus",
    "detail.owner": "Pemilik",
    "detail.condition": "Keadaan",
    "detail.description": "Penerangan",
    "detail.significance": "Kepentingan sejarah",
    "detail.latitude": "Latitud",
    "detail.longitude": "Longitud",

    "form.new": "Rekod baharu",
    "form.edit": "Sunting rekod",
    "form.title.new": "Tambah rekod warisan",
    "form.title.default": "Rekod warisan",
    "form.subtitle":
      "Semua medan bertanda asterisk adalah wajib. Koordinat GPS digunakan untuk paparan peta.",
    "form.gallery": "Galeri foto",
    "form.gallery.empty": "Belum ada foto. Foto pertama akan menjadi kulit.",
    "form.upload": "Tambah foto",
    "form.upload.progress": "Memuat naik…",
    "form.upload.hint": "JPG, PNG atau WEBP. Maksimum 8MB setiap satu. Pilih beberapa untuk muat naik sekali gus.",
    "form.cover": "Kulit",
    "form.field.name": "Nama *",
    "form.field.type": "Jenis *",
    "form.field.description": "Penerangan",
    "form.field.significance": "Kepentingan sejarah",
    "form.field.location": "Lokasi",
    "form.field.location.placeholder": "cth. Padungan, Kuching",
    "form.field.lat": "Latitud *",
    "form.field.lng": "Longitud *",
    "form.field.managed": "Penghuni / Pengurus",
    "form.field.owner": "Pemilik",
    "form.field.authority": "Pihak berkuasa tempatan",
    "form.field.year": "Tahun dibina",
    "form.field.year.placeholder": "cth. 1879",
    "form.field.condition": "Keadaan",
    "form.field.status": "Status",
    "form.field.status.select": "Pilih status",
    "form.field.gazette_no": "No. Warta",
    "form.field.gazette_no.placeholder": "cth. P.U.(B) 123/2021",
    "form.field.land_status": "Status Tanah",
    "form.field.land_status.placeholder": "cth. Tanah Rizab, NCR, Zon Campuran…",
    "detail.status": "Status",
    "detail.gazette_no": "No. Warta",
    "detail.land_status": "Status Tanah",
    "status.Gazetted": "Diwarta",
    "status.Director List": "Senarai Pengarah",
    "status.Not Listed": "Tidak Disenaraikan",
    "form.section.ms": "Bahasa Malaysia (pilihan)",
    "form.section.ms.hint":
      "Terjemahan Bahasa dipaparkan kepada pelawat apabila mereka menukar bahasa. Medan kosong akan menggunakan versi Inggeris.",
    "form.translate": "Terjemah automatik ke Bahasa",
    "form.translate.progress": "Menterjemah…",
    "form.translate.success": "{n} medan diterjemah ke Bahasa",
    "form.translate.failed": "Terjemahan gagal",
    "form.field.name.ms": "Nama (Bahasa)",
    "form.field.description.ms": "Penerangan (Bahasa)",
    "form.field.significance.ms": "Kepentingan sejarah (Bahasa)",
    "form.field.location.ms": "Lokasi (Bahasa)",
    "form.field.managed.ms": "Penghuni / Pengurus (Bahasa)",
    "form.field.owner.ms": "Pemilik (Bahasa)",
    "form.cancel": "Batal",
    "form.save.new": "Cipta rekod",
    "form.save.edit": "Simpan perubahan",
    "form.saving": "Menyimpan…",
    "form.err.name": "Nama diperlukan",

    "delete.title": "Padam rekod warisan?",
    "delete.body.a": "Anda akan memadam secara kekal",
    "delete.body.b": "Tindakan ini tidak boleh dibuat asal.",
    "delete.cancel": "Batal",
    "delete.confirm": "Padam kekal",

    "login.back": "Kembali ke layar",
    "login.back.short": "Kembali",
    "login.editorial.title.a": "Penjaga",
    "login.editorial.title.b": "ingatan bandar.",
    "login.editorial.body":
      "Portal ini khas untuk pentadbir warisan yang diberi kuasa. Log masuk untuk menambah, menyunting dan menjaga katalog pra-inventori bagi DBKU dan MBKS.",
    "login.editorial.footer": "Sarawak · sejak 1841 · arkib hidup",
    "login.overline": "Selamat kembali",
    "login.title": "Akses inventori.",
    "login.subtitle": "Log masuk untuk menyumbang tapak baharu, menyunting rekod anda sendiri, atau membantu memelihara arkib hidup Kuching — semua dialu-alukan.",
    "login.email": "E-mel",
    "login.password": "Kata laluan",
    "login.name": "Nama penuh",
    "login.submit": "Log masuk",
    "login.submitting": "Sedang log masuk…",
    "login.welcome": "Selamat kembali",
    "login.failed": "Log masuk gagal",
    "login.mode.signin": "Log masuk",
    "login.mode.register": "Cipta akaun",
    "login.switch.toRegister": "Baru di sini?",
    "login.switch.toRegisterCta": "Cipta akaun",
    "login.switch.toSignin": "Sudah ada akaun?",
    "login.switch.toSigninCta": "Log masuk",
    "login.register.overline": "Cipta akaun",
    "login.register.title": "Sertai arkib.",
    "login.register.subtitle": "Daftar untuk menyimpan tapak kegemaran dan ikuti perkembangan katalog.",
    "login.register.submit": "Cipta akaun",
    "login.register.submitting": "Mencipta akaun…",
    "login.register.welcome": "Selamat datang ke Warisan Kuching",
    "login.register.failed": "Pendaftaran gagal",
    "login.forgot.link": "Lupa kata laluan?",
    "login.forgot.overline": "Set semula kata laluan",
    "login.forgot.title": "Set semula kata laluan anda.",
    "login.forgot.subtitle":
      "Masukkan e-mel yang dikaitkan dengan akaun anda. Kami akan menghantar pautan selamat untuk memilih kata laluan baharu.",
    "login.forgot.submit": "Hantar pautan set semula",
    "login.forgot.submitting": "Sedang menghantar…",
    "login.forgot.back": "Kembali ke log masuk",
    "login.forgot.sent.title": "Semak peti masuk anda.",
    "login.forgot.sent.body":
      "Jika akaun dengan e-mel itu wujud, kami telah menghantar pautan set semula. Pautan sah selama satu jam.",
    "reset.overline": "Pilih kata laluan baharu",
    "reset.title": "Tetapkan kata laluan baharu.",
    "reset.subtitle": "Pilih kata laluan yang kukuh (sekurang-kurangnya 6 aksara). Anda akan dilog keluar dari sebarang sesi aktif.",
    "reset.new": "Kata laluan baharu",
    "reset.confirm": "Sahkan kata laluan",
    "reset.submit": "Kemaskini kata laluan",
    "reset.submitting": "Mengemaskini…",
    "reset.mismatch": "Kata laluan tidak sepadan",
    "reset.invalid": "Pautan set semula ini tidak sah atau telah tamat tempoh.",
    "reset.success.title": "Kata laluan dikemaskini.",
    "reset.success.body": "Anda kini boleh log masuk dengan kata laluan baharu anda.",
    "reset.success.cta": "Pergi ke log masuk",

    "admin.overline": "Papan pemuka kurator",
    "admin.title": "Selamat datang, {name}.",
    "admin.subtitle":
      "Gambaran keseluruhan katalog pra-inventori merentasi DBKU, MBKS dan pihak berkuasa rakan.",
    "admin.new": "Rekod warisan baharu",
    "admin.export": "Eksport CSV",
    "admin.stat.total": "Jumlah rekod",
    "admin.stat.authorities": "Pihak berkuasa",
    "admin.stat.types": "Jenis diliputi",
    "admin.stat.risk": "Berisiko (Buruk + Kritikal)",
    "admin.breakdown.authority": "Mengikut pihak berkuasa tempatan",
    "admin.breakdown.type": "Mengikut jenis warisan",
    "admin.records.overline": "Inventori",
    "admin.records.title": "Semua rekod",
    "admin.records.public": "Paparan awam",
    "admin.records.empty.title": "Belum ada rekod",
    "admin.records.empty.body": "Tambah rekod warisan pertama anda untuk bermula.",
    "admin.records.loading": "Memuatkan…",
    "admin.col.record": "Rekod",
    "admin.col.type": "Jenis",
    "admin.col.authority": "Pihak berkuasa",
    "admin.col.year": "Tahun",
    "admin.col.condition": "Keadaan",
    "admin.col.actions": "Tindakan",

    "toast.saved.new": "Rekod dicipta",
    "toast.saved.edit": "Rekod dikemas kini",
    "toast.deleted": "Rekod dipadam",
    "toast.load.failed": "Gagal memuat rekod",
    "toast.delete.failed": "Gagal memadam",
    "toast.upload.success": "{n} imej dimuat naik",
    "toast.upload.plural": "{n} imej dimuat naik",
    "toast.upload.failed": "Muat naik gagal",

    "footer.brand": "Warisan Kuching",
    "footer.credit": "Dikurasi untuk DBKU, MBKS & rakan warisan",
    "footer.tagline": "Sistem Pra-Inventori Warisan",
  },
};

export function makeT(lang) {
  return (key, vars) => {
    const bundle = DICT[lang] || DICT.en;
    let s = bundle[key] || DICT.en[key] || key;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        s = s.replace(`{${k}}`, String(v));
      });
    }
    return s;
  };
}

/**
 * Return the localized value for a translatable heritage field.
 * Falls back to the English value if the _ms field is empty.
 */
export function pickLocalized(item, field, lang) {
  if (!item) return "";
  if (lang === "ms") {
    const v = item[`${field}_ms`];
    if (v && String(v).trim().length > 0) return v;
  }
  return item[field] ?? "";
}

// Localised type / condition / authority labels
export const TYPE_LABELS = {
  en: {
    Building: "Building",
    Monument: "Monument",
    Site: "Site",
  },
  ms: {
    Building: "Bangunan",
    Monument: "Monumen",
    Site: "Tapak",
  },
};

export const CONDITION_LABELS = {
  en: {
    Excellent: "Excellent",
    Good: "Good",
    Fair: "Fair",
    Poor: "Poor",
    Critical: "Critical",
  },
  ms: {
    Excellent: "Cemerlang",
    Good: "Baik",
    Fair: "Sederhana",
    Poor: "Buruk",
    Critical: "Kritikal",
  },
};

export function labelType(t, lang) {
  return (TYPE_LABELS[lang] || TYPE_LABELS.en)[t] || t;
}
export function labelCondition(c, lang) {
  return (CONDITION_LABELS[lang] || CONDITION_LABELS.en)[c] || c;
}
